import { CustomDomainStatus } from '@/src/graphql/generated/graphql'
import { validateLinkedProject } from '@/src/graphql/queries/validate-linked-project'
import { ensureLoggedInAsync } from '@/src/user/check-session'
import { initUserStore } from '@/src/user/user-store'
import { initProjectConfig } from '@/src/utils/docs/init-configuration'
import { FileLogger } from '@/src/utils/file-logger'
import { getHashnodeConfig } from '@/src/utils/get-project-config'
import { createConfigurationFolder } from '@/src/utils/hashnode-level-configuration'
import { checkProject, DocsProjectCheckType } from '@hashnode/docs'
import { getLogger } from '@hashnode/file-parsing'
import { Command } from 'commander'
import { assert } from 'console'
import ora from 'ora'
import path from 'path'

let cwd: string

/**
 *
 * TODO:
 * - Check for missing files
 * - Check for corrupted config
 * - Check for invalid file structure
 * - Check for duplicate slugs
 * - Check for duplicate paths
 *
 */

const consoleLogger = getLogger()

const mapStatus = (status: CustomDomainStatus): 'VERIFIED' | 'PENDING' | 'FAILED' => {
	switch (status) {
		case CustomDomainStatus.Valid:
			return 'VERIFIED'
		case CustomDomainStatus.Verifying:
			return 'PENDING'
		case CustomDomainStatus.Invalid:
			return 'FAILED'
	}
}

export const verify = new Command()
	.name('verify')
	.description('Check your local changes for any errors')
	.option(
		'-c, --cwd <cwd>',
		'the working directory. defaults to the current directory.',
		process.cwd(),
	)
	.option('-o, --write-file', 'write the result to a file')
	.action(async (options) => {
		cwd = path.resolve(options.cwd)

		const logger = options.writeFile ? new FileLogger(cwd) : getLogger()

		await createConfigurationFolder(cwd)

		initUserStore(cwd)

		await ensureLoggedInAsync()

		let config = getHashnodeConfig(cwd)
		if (!config || !config.linkedProjectId) {
			config = await initProjectConfig(cwd)
		}
		assert(config?.linkedProjectId, 'Linked project ID is missing')

		// If the linked project is not found, exit the process
		const linkedProject = await validateLinkedProject(config.linkedProjectId)

		assert(linkedProject, 'Linked project not found')

		if (!linkedProject) {
			throw new Error('Linked project not found')
		}

		const spinner = ora(' Validating local files... ').start()

		const docsProject: DocsProjectCheckType = {
			id: linkedProject.id,
			guides: linkedProject.guides
				.filter((guide) => guide.status !== 'DELETED')
				.map((guide) => ({
					id: guide.id,
					slug: guide.slug,
					provider: guide.provider,
				})),
			customDomain: linkedProject.domain?.customDomain
				? {
						domain: linkedProject.domain!.customDomain!.domain,
						status: mapStatus(linkedProject.domain!.customDomain!.status),
					}
				: undefined,
			subdomain: linkedProject.domain!.hashnodeSubDomain,
		}

		const { errors, hasErrors } = checkProject({
			folderPath: cwd,
			logger,
			exitingProject: docsProject,
		})

		spinner.info('Check completed')

		if (!hasErrors) {
			spinner.succeed('No errors found in the documentation.')
		} else {
			spinner.fail('Errors found in the documentation.')
		}

		if (errors.configurationErrors.hasErrors) {
			logger.info('--------------------------------')
			logger.error('Configuration errors:\n')
			logger.log(errors.configurationErrors.errorOutput)
		}

		if (errors.contentErrors.hasErrors) {
			logger.info('--------------------------------')
			logger.error('Content errors:\n')
			logger.log(errors.contentErrors.errorOutput)
		}

		if (errors.duplicatedSlugs.hasErrors) {
			logger.info('--------------------------------')
			logger.error('Duplicated slugs:\n')
			logger.log(errors.duplicatedSlugs.errorOutput)
		}

		if (errors.duplicatedPaths.hasErrors) {
			logger.info('--------------------------------')
			logger.error('Duplicated paths:\n')
			logger.log(errors.duplicatedPaths.errorOutput)
		}

		if (errors.missingFiles.hasErrors) {
			logger.info('--------------------------------')
			logger.error('Missing files:\n')
			logger.log(errors.missingFiles.errorOutput)
		}
		if (logger instanceof FileLogger) {
			logger.info('--------------------------------')
			if (hasErrors) {
				logger.writeBufferToFile()
				consoleLogger.info(`Wrote results to file ${FileLogger.getFilePath(cwd)}`)
			} else {
				consoleLogger.info(`No errors found, not writing to file`)
			}
		}

		// Check for any missing files and relative urls in the project

		process.exit(0)
	})
